// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useRef, useEffect, CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Globe, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLanguage } from './use-language'

interface LanguageSelectorProps {
  className?: string
  variant?: 'default' | 'ghost'
}

export function LanguageSelector({ className, variant = 'default' }: LanguageSelectorProps) {
  const { current, setLanguage, languages } = useLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({})

  function handleToggle() {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
        zIndex: 9999,
      })
    }
    setOpen(o => !o)
  }

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      const target = e.target as Node
      const insideTrigger = ref.current?.contains(target) ?? false
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      if (!insideTrigger && !insideDropdown) setOpen(false)
    }
    document.addEventListener('mousedown', onOutsideClick)
    return () => document.removeEventListener('mousedown', onOutsideClick)
  }, [])

  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={handleToggle}
        aria-label="Select language"
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors',
          variant === 'ghost'
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : 'bg-secondary border border-border hover:border-primary/40 text-foreground',
        )}
      >
        <Globe className="w-4 h-4 flex-shrink-0" />
        <span>{current.nativeName}</span>
        <ChevronDown className={cn('w-3.5 h-3.5 opacity-60 transition-transform', open && 'rotate-180')} />
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="min-w-[180px] bg-card border border-border rounded-xl shadow-lg shadow-black/10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
        >
          {languages.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => { setLanguage(lang.code); setOpen(false) }}
              className={cn(
                'w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors',
                lang.code === current.code
                  ? 'bg-primary/8 text-primary font-semibold'
                  : 'text-foreground hover:bg-secondary',
              )}
            >
              <span>{lang.nativeName}</span>
              <span className="text-muted-foreground text-xs ml-3">{lang.name}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}
